import { useMemo, useRef } from 'react'
import { toast } from 'sonner'

import { getErrorMessage } from '@/lib/api'

import type { RealtimeDataSource } from '../api/data-source'
import { useRealtimeStore } from '../store/realtime-store'
import type { Job } from '../types'
import { jobHasWorker } from '../utils/job-workers'
import { clearLocalMutation, registerLocalMutation, type LocalMutationKind } from './local-mutations'
import { beginStaffingMutation, endStaffingMutation } from './staffing-guard'

type StaffingSnapshot = Pick<Job, 'id' | 'workers' | 'requiredWorkers' | 'status'>

const snapshotOf = (job: Job): StaffingSnapshot => ({ id: job.id, workers: job.workers, requiredWorkers: job.requiredWorkers, status: job.status })

/**
 * Optimistic add / remove. The store changes immediately (card, popup, pins, routes and the
 * nearby strip update at once), the API call runs in the background, and on failure only the
 * job's staffing is rolled back (other live updates to the job are kept) with an error toast.
 * Outcomes are also announced in the page's aria-live region.
 */
export function useJobWorkerActions(
  source: RealtimeDataSource,
  {
    onStaffingChanged,
  }: {
    /** Called after a successful Assign / Remove, to re-read assignments right away. */
    onStaffingChanged?: (jobId: string) => void
  } = {},
) {
  // Latest callback without re-creating the actions.
  const onChangedRef = useRef(onStaffingChanged)
  onChangedRef.current = onStaffingChanged

  return useMemo(() => {
    const store = () => useRealtimeStore.getState()
    const nameOf = (workerId: string) => store().workersById[workerId]?.name ?? 'Worker'

    async function run(
      kind: LocalMutationKind,
      job: Job,
      workerId: string | undefined,
      optimistic: () => void,
      request: () => Promise<void>,
      messages: { success: string; failure: string },
    ): Promise<boolean> {
      const previous = snapshotOf(job)
      registerLocalMutation(kind, job.id, workerId)
      beginStaffingMutation(job.id)
      optimistic()
      try {
        await request()
        endStaffingMutation(job.id, true)
        toast.success(messages.success)
        store().announce(messages.success)
        onChangedRef.current?.(job.id)
        return true
      } catch (error) {
        endStaffingMutation(job.id, false)
        clearLocalMutation(kind, job.id, workerId)
        store().rollbackJobStaffing(previous)
        toast.error(messages.failure, { description: getErrorMessage(error, 'The change was rolled back. Please try again.') })
        store().announce(`${messages.failure}. The change was rolled back.`)
        return false
      }
    }

    const addWorker = (job: Job, workerId: string) => {
      if (jobHasWorker(job, workerId)) {
        toast.info(`${nameOf(workerId)} is already on ${job.bookingNo}`)
        return Promise.resolve(false)
      }
      const at = new Date().toISOString()
      return run(
        'add', job, workerId,
        () => store().applyEvents([{ type: 'job.worker.added', jobId: job.id, worker: { workerId, response: 'pending', status: 'assigned', sentAt: at } }]),
        () => source.addJobWorker({ job, workerId }),
        { success: `${nameOf(workerId)} added to ${job.bookingNo}`, failure: `Couldn't add ${nameOf(workerId)}` },
      )
    }

    const removeWorker = (job: Job, workerId: string, details: { reason?: string | null; note?: string | null } = {}) => run(
      'remove', job, workerId,
      () => store().applyEvents([{ type: 'job.worker.removed', jobId: job.id, workerId, reason: details.reason ?? null }]),
      () => source.removeJobWorker({ job, workerId, ...details }),
      { success: `${nameOf(workerId)} removed from ${job.bookingNo}`, failure: `Couldn't remove ${nameOf(workerId)}` },
    )

    return { addWorker, removeWorker }
  }, [source])
}

export type JobWorkerActions = ReturnType<typeof useJobWorkerActions>
